import dayjs from "dayjs";
import { applyFilters, getFieldValue } from "../common/searchFilters";
import { HomeSitesDataIndex } from "../../reactQuery/homeSitesDataIndex/types";

export const isEcoeMismatch = (hs: HomeSitesDataIndex) => {
  const hasSaleDate = hs.saleDate != null;
  const noCloseOfEscrowActual = hs.closeOfEscrowActual == null;

  if (!hasSaleDate || !noCloseOfEscrowActual) return false;

  const jdeEcoe = hs.closeOfEscrowEstimated;
  const mortgageEcoe = hs.closeOfEscrowEstimatedMortgage;

  if (!jdeEcoe && !mortgageEcoe) return false;
  if (!mortgageEcoe || !jdeEcoe) return true;

  const jdeDate = dayjs(jdeEcoe);
  const mortgageDate = dayjs(mortgageEcoe);

  if (!jdeDate.isValid() || !mortgageDate.isValid()) return true;

  return !jdeDate.isSame(mortgageDate, "day");
};
 
export const calculateMetrics = (homesites: HomeSitesDataIndex[], filters: Record<string, any>) => {
  if (!homesites || homesites.length === 0) return getDefaultMetrics();
  
  const today = dayjs().startOf("day");
  const last7DaysStart = today.subtract(7, "day").toDate();
  const last30DaysStart = today.subtract(30, "day").toDate();
  const startOfMonth = today.startOf("month").toDate();
  const nextMonth = today.startOf("month").add(1, "month").toDate();
  const todayDate = today.toDate();
  const closings14DaysEnd = dayjs(todayDate).add(15, "day").toDate();
  const closings30DaysEnd = dayjs(todayDate).add(31, "day").toDate();
  const closings60DaysEnd = dayjs(todayDate).add(61, "day").toDate();
  const lastContactThreshold = today.subtract(7, "day").toDate();

  // Initialize result arrays
  const allBuyersIds: string[] = [];
  const newSalesLast7DaysIds: string[] = [];
  const weeklyUpdateIds: string[] = [];
  const closingsIn14DaysIds: string[] = [];
  const closingsThisMonthIds: string[] = [];
  const closingsIn60DaysIds: string[] = [];
  const closingsIn30DaysIds: string[] = [];
  const closeHomeSitesIds: string[] = [];
  const ecoeMismatchIds: string[] = [];

  // Single pass through the array
  for (const hs of homesites) {
    const hasSaleDate = hs.saleDate != null;
    const hasCloseOfEscrowActual = hs.closeOfEscrowActual != null;
    const noCloseOfEscrowActual = hs.closeOfEscrowActual == null;

    // Parse dates once and cache
    const saleDateParsed = hasSaleDate ? dayjs(hs.saleDate) : null;
    const closeOfEscrowEstimatedParsed = hs.closeOfEscrowEstimated ? dayjs(hs.closeOfEscrowEstimated) : null;
    const closeOfEscrowActualParsed = hasCloseOfEscrowActual ? dayjs(hs.closeOfEscrowActual) : null;
    const lastContactParsed = hs.lastContact ? dayjs(hs.lastContact) : null;
    // allBuyers: closeOfEscrowActual == null && saleDate != null
    if (noCloseOfEscrowActual && hasSaleDate) {
      allBuyersIds.push(hs.homeSiteId);
    }

    // newSalesLast7Days: saleDate in last 7 days && closeOfEscrowActual == null
    if (hasSaleDate && noCloseOfEscrowActual && saleDateParsed?.isValid()) {
      if (saleDateParsed.isSameOrAfter(last7DaysStart)) {
        newSalesLast7DaysIds.push(hs.homeSiteId);
      }
    }

    // weeklyUpdate: (lastContact == null || lastContact < 7 days ago) && saleDate != null && closeOfEscrowActual == null
    if (hasSaleDate && noCloseOfEscrowActual) {
      const lastContactOld = !lastContactParsed || (lastContactParsed.isValid() && lastContactParsed.isBefore(lastContactThreshold));
      if (lastContactOld) {
        weeklyUpdateIds.push(hs.homeSiteId);
      }
    }

    // closingsIn14Days: closeOfEscrowEstimated within 15 days && saleDate != null && closeOfEscrowActual == null
    if (hasSaleDate && noCloseOfEscrowActual && closeOfEscrowEstimatedParsed?.isValid()) {
      if (closeOfEscrowEstimatedParsed.isSameOrAfter(todayDate) && closeOfEscrowEstimatedParsed.isBefore(closings14DaysEnd)) {
        closingsIn14DaysIds.push(hs.homeSiteId);
      }
    }

    // closingsThisMonth: closeOfEscrowEstimated in current month && closeOfEscrowActual == null
    if (noCloseOfEscrowActual && closeOfEscrowEstimatedParsed?.isValid()) {
      if (closeOfEscrowEstimatedParsed.isSameOrAfter(startOfMonth) && closeOfEscrowEstimatedParsed.isBefore(nextMonth)) {
        closingsThisMonthIds.push(hs.homeSiteId);
      }
    }

    // closingsIn60Days: closeOfEscrowEstimated within 61 days && saleDate != null && closeOfEscrowActual == null
    if (hasSaleDate && noCloseOfEscrowActual && closeOfEscrowEstimatedParsed?.isValid()) {
      if (closeOfEscrowEstimatedParsed.isSameOrAfter(todayDate) && closeOfEscrowEstimatedParsed.isBefore(closings60DaysEnd)) {
        closingsIn60DaysIds.push(hs.homeSiteId);
      }
    }

    // closingsIn30Days: closeOfEscrowEstimated within 31 days && saleDate != null && closeOfEscrowActual == null
    if (hasSaleDate && noCloseOfEscrowActual && closeOfEscrowEstimatedParsed?.isValid()) {
      if (closeOfEscrowEstimatedParsed.isSameOrAfter(todayDate) && closeOfEscrowEstimatedParsed.isBefore(closings30DaysEnd)) {
        closingsIn30DaysIds.push(hs.homeSiteId);
      }
    }

    // closeHomeSites: (closeOfEscrowActual or saleDate) in last 30 days && closeOfEscrowActual != null
    if (hasCloseOfEscrowActual) {
      if (closeOfEscrowActualParsed?.isValid() && closeOfEscrowActualParsed.isSameOrAfter(last30DaysStart)) {
        closeHomeSitesIds.push(hs.homeSiteId);
      } else if (hasSaleDate && saleDateParsed?.isValid() && saleDateParsed.isSameOrAfter(last30DaysStart)) {
        closeHomeSitesIds.push(hs.homeSiteId);
      }
    }

    // ECOE Mismatch: (Mismatch between JDE Est COE and Mortgage ECOE)
    if (isEcoeMismatch(hs)) {
      ecoeMismatchIds.push(hs.homeSiteId);
    }
  }

  return {
    allBuyers: { count: allBuyersIds.length, homeSiteIds: allBuyersIds },
    newSalesLast7Days: { count: newSalesLast7DaysIds.length, homeSiteIds: newSalesLast7DaysIds },
    weeklyUpdate: { count: weeklyUpdateIds.length, homeSiteIds: weeklyUpdateIds },
    closingsIn14Days: { count: closingsIn14DaysIds.length, homeSiteIds: closingsIn14DaysIds },
    closingsThisMonth: { count: closingsThisMonthIds.length, homeSiteIds: closingsThisMonthIds },
    closingsIn60Days: { count: closingsIn60DaysIds.length, homeSiteIds: closingsIn60DaysIds },
    closingsIn30Days: { count: closingsIn30DaysIds.length, homeSiteIds: closingsIn30DaysIds },
    closeHomeSites: { count: closeHomeSitesIds.length, homeSiteIds: closeHomeSitesIds },
    ecoeMismatch: { count: ecoeMismatchIds.length, homeSiteIds: ecoeMismatchIds},
  };
};

const getDefaultMetrics = () => {
  const defaultMetric = { count: 0, homeSiteIds: [] };
  return {
    allBuyers: defaultMetric,
    newSalesLast7Days: defaultMetric,
    weeklyUpdate: defaultMetric,
    closingsIn14Days: defaultMetric,
    closingsThisMonth: defaultMetric,
    closingsIn60Days: defaultMetric,
    closingsIn30Days: defaultMetric,
    closeHomeSites: defaultMetric,
    ecoeMismatch: defaultMetric
  };
};

export const weeklyUpdate = (homesites: HomeSitesDataIndex[], today: dayjs.Dayjs) => {
  const lastContactThreshold = today.subtract(7, "day").toDate();
  const homeSiteIds: string[] = [];
  
  for (const hs of homesites) {
    if (hs.saleDate != null && hs.closeOfEscrowActual == null) {
      const lastContactOld = !hs.lastContact || (dayjs(hs.lastContact).isValid() && dayjs(hs.lastContact).isBefore(lastContactThreshold));
      if (lastContactOld) {
        homeSiteIds.push(hs.homeSiteId);
      }
    }
  }
  
  return {
    count: homeSiteIds.length,
    homeSiteIds,
  };
};

export const filterByDate = (homesites: HomeSitesDataIndex[], startDate: Date, endDate?: Date | null, keys: string[] = [], additionalConditions: (hs: HomeSitesDataIndex) => boolean = () => true) => {
  const homeSiteIds: string[] = [];
  
  for (const hs of homesites) {
    let matches = false;
    for (const key of keys) {
      const value = getFieldValue(hs, key);
      if (!value) continue;
      
      const parsedDate = dayjs(value);
      if (!parsedDate.isValid()) continue;
      
      const isWithinRange = parsedDate.isSameOrAfter(startDate) && (!endDate || parsedDate.isBefore(endDate));
      if (isWithinRange && additionalConditions(hs)) {
        matches = true;
        break;
      }
    }
    if (matches) {
      homeSiteIds.push(hs.homeSiteId);
    }
  }
  
  return {
    count: homeSiteIds.length,
    homeSiteIds,
  };
};

export const filterByDateRange = (homesites: HomeSitesDataIndex[], startDate: Date, endDate: Date | null, keys: string[], additionalConditions: (hs: HomeSitesDataIndex) => boolean = () => true) => {
  return filterByDate(homesites, startDate, endDate, keys, hs =>
    hs.saleDate != null && additionalConditions(hs)
  );
};

export const calculateClosings = (homesites: HomeSitesDataIndex[], days: number, startDate: Date) => {
  const endDate = dayjs(startDate).add(days, "day").toDate();
  return filterByDate(homesites, startDate, endDate, ["closeOfEscrowEstimated"], hs =>
    hs.saleDate != null && hs.closeOfEscrowActual == null
  );
};

export const filterByKeyValues = (homesites: HomeSitesDataIndex[], filters: Record<string, any>) => {
  const filteredHomesites = applyFilters(homesites, filters);
  return {
    count: filteredHomesites.length,
    homeSiteIds: filteredHomesites.map(hs => hs.homeSiteId),
  };
};



